import { StyleProvider } from "@ant-design/cssinjs"
import { App as AntdApp, ConfigProvider } from "antd"
import zh_TW from "antd/es/locale/zh_TW"
import { useEffect } from "react"
import { createRoot } from "react-dom/client"
import { HelmetProvider } from "react-helmet-async"
import { Provider } from "react-redux"
import { BrowserRouter } from "react-router-dom"
import App from "./App"
import configureStore from "./store/configureStore"
import "./style/tailwind.css"

import createCache from "@emotion/cache"
import { CacheProvider } from "@emotion/react"
import { setNonce } from "get-nonce"

import { config } from "@fortawesome/fontawesome-svg-core"
import "@fortawesome/fontawesome-svg-core/styles.css"
import { PersistGate } from "redux-persist/lib/integration/react"
import { setNotificationInstance } from "./common/notification"
config.autoAddCss = false

// 將 antd <App> 的 context-aware notification 實例注入全域 holder,
// 供元件外程式(api 攔截器、模組層級 helper)使用
function NotificationBridge() {
  const { notification } = AntdApp.useApp()
  useEffect(() => {
    setNotificationInstance(notification)
  }, [notification])
  return null
}

function MyEmotionProvider({ nonce, children }) {
  const cache = createCache({
    key: "avm-css-nonce",
    nonce
  })

  setNonce(nonce)

  return <CacheProvider value={cache} children={children} />
}

const CSP_NONCE = process.env.REACT_APP_CSP_NONCE

// 攔截所有動態插入的 <style> 標籤，自動加上 nonce（解決 antd v6 CSP 限制）
function injectNonce(node) {
  if (node && node.tagName === "STYLE" && !node.nonce) {
    node.nonce = CSP_NONCE
  }
}

const _originalInsertBefore = Element.prototype.insertBefore
Element.prototype.insertBefore = function (newNode, referenceNode) {
  injectNonce(newNode)
  return _originalInsertBefore.call(this, newNode, referenceNode)
}

const _originalAppendChild = Element.prototype.appendChild
Element.prototype.appendChild = function (child) {
  injectNonce(child)
  return _originalAppendChild.call(this, child)
}

const _originalPrepend = Element.prototype.prepend
Element.prototype.prepend = function (...nodes) {
  nodes.forEach(injectNonce)
  return _originalPrepend.apply(this, nodes)
}

const { store, persistor } = configureStore()

const rootElement = document.getElementById("root")
const root = createRoot(rootElement)

const renderApp = () =>
  root.render(
    // 暫時移除 StrictMode 避免 React 18 雙重執行 cleanup 導致的錯誤
    // <React.StrictMode>
    <HelmetProvider>
      <Provider store={store}>
        <StyleProvider nonce={CSP_NONCE}>
          <ConfigProvider
            csp={{ nonce: CSP_NONCE }}
            locale={zh_TW}
            theme={{}}
          >
            <AntdApp component={false}>
              <NotificationBridge />
              <PersistGate loading={null} persistor={persistor}>
                <MyEmotionProvider nonce={CSP_NONCE}>
                  <BrowserRouter
                    future={{
                      v7_startTransition: true,
                      v7_relativeSplatPath: true
                    }}
                  >
                    <App />
                  </BrowserRouter>
                </MyEmotionProvider>
              </PersistGate>
            </AntdApp>
          </ConfigProvider>
        </StyleProvider>
      </Provider>
    </HelmetProvider>
    // </React.StrictMode>
  )

if (process.env.NODE_ENV !== "production" && module.hot) {
  module.hot.accept("./App", renderApp)
}

renderApp()
